import { FetchArgs } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/react";
import { maskSensitiveData } from "@utils/misc";
import { APIErrorCodes } from "kz-ui-sdk";
import { isNil, omitBy } from "lodash-es";
import { getSentryManual } from "./manual-sentry-lazy";

export const API_ERROR_TYPE = {
  SERVER_ERROR: "Server error",
  API_ERROR: "API error",
};

const SENSITIVE_FIELDS = [
  "password",
  "pin",
  "client_secret",
  "phone",
  "client_id",
  "accNo",
  "key",
  "firstname",
  "lastname",
  "secret",
  "code",
  "seonSession",
];

const IGNORE_ERROR_MESSAGES = [
  APIErrorCodes.Auth.Required,
  APIErrorCodes.Auth.TokenExpired,
  APIErrorCodes.Auth.TokenInvalidated,
  APIErrorCodes.Auth.InvalidCredential,
  APIErrorCodes.Auth.InvalidCredentials,
];

const IGNORE_ENDPOINTS = ["/member/referral/reward/daily-claim", "/member/game/gateway"];

export const standardizeSentryRequest = (requestBody?: any): any => {
  return maskSensitiveData(requestBody, SENSITIVE_FIELDS, "******");
};

export const captureApiException = (args: FetchArgs, response: any, apiErrorType: string) => {
  const url = args?.url;
  const request = args?.body;
  const statusCode = response?.status;
  const errorType = response?.data?.error?.type;

  const error = new Error(`${url}`);
  error.name = "ApiCallFailedError";

  // ignore network error
  if (response?.data?.error === "Failed to fetch") {
    return;
  }

  // ignore non response error
  if (!response?.data) {
    return;
  }

  // ignore ignore error messages
  if (IGNORE_ERROR_MESSAGES.includes(response?.data?.error?.message)) {
    return;
  }

  // ignore ignore endpoints start with
  if (IGNORE_ENDPOINTS.some((endpoint) => url.startsWith(endpoint))) {
    return;
  }

  Sentry.captureException(error, {
    extra: {
      request: standardizeSentryRequest(request),
      response: response?.data,
    },
    tags: omitBy(
      {
        endpoint: url,
        errorType: errorType,
        statusCode: statusCode,
      },
      isNil,
    ),
  });
};

export const capturePageCrashError = async (
  error: unknown,
  extra?: Record<string, unknown>,
): Promise<string | undefined> => {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  errorObj.name = "Page Crashes Error";

  const errorMessage = errorObj.message || String(error);
  const errorStack = errorObj.stack || "";
  const errorName = errorObj.name || "Error";

  const manual = await getSentryManual();
  return manual.captureMessage(errorName, "error", {
    error_message: errorMessage,
    error_stack: errorStack,
    original_error: errorObj.toString(),
    ...extra,
  });
};
